import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Plus, Send, Search } from "lucide-react";
import { waeAPI, STATUS_STYLE, STATUS_LABEL, PRIORITY_STYLE, fmtDT } from "./waeApi";
import { Modal, Fld, Footer, EmptyState, cls } from "./uiKit";
import RequestDrawer from "./RequestDrawer";
import { NumberInput } from "@/components/ui/number-input";

const STATUS_TABS = [
  { k: "",           label: "All" },
  { k: "pending",    label: "Pending" },
  { k: "approved",   label: "Approved" },
  { k: "rejected",   label: "Rejected" },
  { k: "sent_back",  label: "Sent Back" },
  { k: "cancelled",  label: "Cancelled" },
];

export default function WAEMyRequests() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);
  const [sp, setSp] = useSearchParams();

  const load = () => waeAPI.myRequests({ status: status || undefined }).then(setRows).catch(()=>{});
  useEffect(() => { load(); }, [status]);   // eslint-disable-line
  useEffect(() => {
    if (sp.get("new") === "1") { setCreating(true); sp.delete("new"); setSp(sp, { replace: true }); }
  }, [sp, setSp]);

  const filtered = useMemo(() =>
    q ? rows.filter(r => r.code.toLowerCase().includes(q.toLowerCase()) ||
                          r.subject.toLowerCase().includes(q.toLowerCase())) : rows
  , [rows, q]);

  return (
    <div className="space-y-4" data-testid="wae-my-requests">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2"/>
          <input value={q} onChange={e=>setQ(e.target.value)} placeholder="Search my requests…" aria-label="Search my requests"
                 data-testid="wae-my-search"
                 className="h-10 pl-9 pr-3 rounded-lg border border-slate-200 bg-white text-[13px] w-72 focus:border-hocp-navy outline-none"/>
        </div>
        <div className="flex gap-1 overflow-x-auto" data-testid="wae-my-tabs">
          {STATUS_TABS.map(t => (
            <button key={t.k} onClick={()=>setStatus(t.k)}
                    data-testid={`wae-my-tab-${t.k || "all"}`}
                    className={`h-9 px-3 rounded-lg text-[12.5px] font-medium border whitespace-nowrap ${
                      status===t.k ? "bg-hocp-mint text-hocp-deepTeal border-hocp-mint" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-50"}`}>
              {t.label}
            </button>
          ))}
        </div>
        <button onClick={()=>setCreating(true)} data-testid="wae-my-new"
                className="ml-auto h-10 px-4 rounded-lg bg-hocp-gold text-hocp-eerie text-[13px] font-semibold inline-flex items-center gap-2 hover:brightness-95">
          <Plus className="w-4 h-4"/> New Request
        </button>
      </div>

      <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
        {filtered.length === 0 ? (
          <EmptyState icon={Send} title="No requests yet"
            msg="Kick off your first workflow — every approval across HOCP flows through this engine."/>
        ) : (
          <table className="w-full text-[13px]">
            <thead className="bg-slate-50 text-slate-500 text-[11px] uppercase tracking-wider">
              <tr>
                <th className="text-left px-4 py-3">Code</th>
                <th className="text-left px-4 py-3">Subject</th>
                <th className="text-left px-4 py-3">Type</th>
                <th className="text-right px-4 py-3">Amount</th>
                <th className="text-center px-4 py-3">Priority</th>
                <th className="text-center px-4 py-3">Status</th>
                <th className="text-left px-4 py-3">Stage</th>
                <th className="text-left px-4 py-3">Created</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {filtered.map(r => (
                <tr key={r.id} onClick={()=>setOpenId(r.id)} data-testid={`wae-my-row-${r.id}`}
                    className="cursor-pointer hover:bg-slate-50/70">
                  <td className="px-4 py-3 mono font-medium text-hocp-navy">{r.code}</td>
                  <td className="px-4 py-3 max-w-xs truncate font-medium text-hocp-navy">{r.subject}</td>
                  <td className="px-4 py-3 text-[12px] text-slate-600">{r.approval_type_name}</td>
                  <td className="px-4 py-3 text-right tabular-nums font-medium">
                    {r.amount != null ? `${r.currency||"INR"} ${Number(r.amount).toLocaleString("en-IN")}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium capitalize ${PRIORITY_STYLE[r.priority]}`}>{r.priority}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`px-2 py-0.5 rounded-md text-[11px] font-medium border ${STATUS_STYLE[r.status] || ""}`}>
                      {STATUS_LABEL[r.status] || r.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[12px] text-slate-500">
                    {(r.levels || [])[r.current_level_index]?.name || (r.status === "approved" ? "Completed" : "—")}
                  </td>
                  <td className="px-4 py-3 text-[12px] text-slate-500 mono">{fmtDT(r.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {creating && <CreateRequestModal onClose={()=>setCreating(false)} onDone={()=>{ setCreating(false); load(); }}/>}
      {openId && <RequestDrawer requestId={openId} currentUserId={user?.id || user?._id}
                                  onClose={()=>setOpenId(null)} onChanged={load}/>}
    </div>
  );
}

function CreateRequestModal({ onClose, onDone }) {
  const [types, setTypes] = useState([]);
  const [f, setF] = useState({
    approval_type_code: "",
    subject: "",
    description: "",
    amount: "",
    currency: "INR",
    priority: "normal",
    entity_ref: "",
    meta: {},
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    waeAPI.approvalTypes().then(list => {
      const active = list.filter(t => t.is_active);
      setTypes(active);
      if (active.length && !f.approval_type_code) {
        setF(prev => ({ ...prev, approval_type_code: active[0].code }));
      }
    });
  }, []); // eslint-disable-line

  const submit = async () => {
    if (!f.approval_type_code || !f.subject.trim()) {
      toast.error("Approval type and subject are required"); return;
    }
    setSaving(true);
    try {
      const body = {
        approval_type_code: f.approval_type_code,
        subject: f.subject.trim(),
        description: f.description.trim() || null,
        priority: f.priority,
        currency: f.currency,
        entity_ref: f.entity_ref.trim() || null,
      };
      if (f.amount) body.amount = Number(f.amount);
      const created = await waeAPI.createRequest(body);
      if (created.status === "approved" && created.levels?.[0]?.key === "AUTO") {
        toast.success(`Auto-approved: ${created.code}`);
      } else {
        toast.success(`Submitted ${created.code}`);
      }
      onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setSaving(false); }
  };

  return (
    <Modal title="New Workflow Request" onClose={onClose} size="2xl">
      <div className="grid grid-cols-2 gap-3">
        <Fld label="Approval Type" full>
          <select value={f.approval_type_code} onChange={e=>setF({...f, approval_type_code: e.target.value})}
                  data-testid="wae-new-type" className={cls}>
            {types.map(t => <option key={t.code} value={t.code}>{t.name}</option>)}
          </select>
        </Fld>
        <Fld label="Subject" full>
          <input value={f.subject} onChange={e=>setF({...f, subject: e.target.value})}
                 data-testid="wae-new-subject" className={cls} placeholder="Short summary of what needs approval"/>
        </Fld>
        <Fld label="Amount (optional)">
          <NumberInput step="0.01" value={f.amount} onChange={e=>setF({...f, amount: e.target.value})}
                 data-testid="wae-new-amount" className={cls}/>
        </Fld>
        <Fld label="Currency">
          <select value={f.currency} onChange={e=>setF({...f, currency: e.target.value})} className={cls}>
            <option>INR</option><option>USD</option><option>EUR</option><option>GBP</option>
          </select>
        </Fld>
        <Fld label="Priority">
          <select value={f.priority} onChange={e=>setF({...f, priority: e.target.value})}
                  data-testid="wae-new-priority" className={cls}>
            <option value="low">Low</option>
            <option value="normal">Normal</option>
            <option value="high">High</option>
            <option value="urgent">Urgent</option>
          </select>
        </Fld>
        <Fld label="Related Entity (optional)">
          <input value={f.entity_ref} onChange={e=>setF({...f, entity_ref: e.target.value})}
                 data-testid="wae-new-entity" className={cls} placeholder="e.g. F00123, PREQ-2026…"/>
        </Fld>
        <Fld label="Description" full>
          <textarea value={f.description} onChange={e=>setF({...f, description: e.target.value})}
                    data-testid="wae-new-description" rows={3}
                    className={cls + " h-auto py-2 resize-y"}
                    placeholder="Context that will help approvers decide."/>
        </Fld>
      </div>
      <Footer onClose={onClose} onSubmit={submit} saving={saving} submitLabel="Submit for Approval" testid="wae-new-submit"/>
    </Modal>
  );
}
